import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { adminApi, errorMessage } from "../../lib/api";

export const AssignPicker = ({ b, employees, onAssign }) => {
  const approved = employees.filter((e) => e.kyc_status === "approved");
  const [sel, setSel] = useState(() => approved.filter((e) => (b.worker_ids || []).includes(e.id)).map((e) => e.reference));
  const [q, setQ] = useState("");
  const [sug, setSug] = useState(null);
  const toggle = (r) => setSel((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));
  const loadSug = () => adminApi.get(`/admin/hk/bookings/${b.id}/suggest`).then((r) => { setSug(r.data); return r.data; });
  const autoPick = () => loadSug().then((d) => {
    const top = d.items.filter((x) => !x.busy).slice(0, d.needed).map((x) => x.reference);
    if (!top.length) { toast.error("No free KYC-approved employee found"); return; }
    setSel(top);
    toast.success(`Suggested: ${top.join(", ")}`);
  }).catch((e) => toast.error(errorMessage(e)));
  const score = (ref) => sug?.items.find((x) => x.reference === ref);
  const shown = approved
    .filter((e) => `${e.full_name} ${e.reference} ${e.job_role} ${e.district}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, c) => (score(c.reference)?.score ?? 0) - (score(a.reference)?.score ?? 0));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover onOpenChange={(o) => { if (o && !sug) loadSug().catch(() => {}); }}>
        <PopoverTrigger asChild>
          <button data-testid={`hk-admin-assign-picker-${b.id}`} className="h-8 min-w-[16rem] border border-white/15 bg-[#0B1120] px-2 text-left text-sm text-slate-300">
            {sel.length ? `${sel.length} selected: ${sel.join(", ")}` : "Choose employees…"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-96 rounded-none border-white/10 bg-[#0F172A] p-2 text-slate-100">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, role, district" data-testid={`hk-admin-assign-search-${b.id}`}
            className="mb-2 h-8 w-full border border-white/15 bg-[#0B1120] px-2 text-sm" />
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {shown.length === 0 && <p className="p-2 text-xs text-slate-500">No KYC-approved employees in Book Service / Housekeeping.</p>}
            {shown.map((e, i) => {
              const s = score(e.reference);
              return (
                <label key={e.id} data-testid={`hk-admin-assign-opt-${b.id}-${e.reference}`} className="flex cursor-pointer items-start gap-2 p-1.5 text-sm hover:bg-white/5">
                  <input type="checkbox" checked={sel.includes(e.reference)} onChange={() => toggle(e.reference)} className="mt-1 accent-orange-500" />
                  <span className="flex-1"><b className="text-white">{e.full_name}</b> <span className="font-mono text-xs text-orange-300">{e.reference}</span>
                    {s && i === 0 && !s.busy && s.score > 0 && <span className="ml-1 bg-emerald-600 px-1 text-[10px] font-bold text-white">BEST</span>}<br />
                    <span className="text-xs text-slate-400">{e.job_role} · {e.district} · {e.today_jobs} job(s) today</span>
                    {s && <span data-testid={`hk-admin-assign-score-${b.id}-${e.reference}`} className={`block text-[11px] ${s.busy ? "text-red-300" : "text-emerald-300"}`}>Match {s.score} · {s.reasons.join(", ")}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <button onClick={autoPick} data-testid={`hk-admin-assign-suggest-${b.id}`} className="flex items-center gap-1 border border-emerald-500/50 px-3 py-1 text-sm text-emerald-300 hover:bg-emerald-500/10">
        <Sparkles className="h-3.5 w-3.5" />Auto suggest
      </button>
      <button disabled={!sel.length} onClick={() => onAssign(sel)} data-testid={`hk-admin-assign-${b.id}`} className="border border-white/15 px-3 py-1 text-sm disabled:opacity-40">Assign</button>
    </div>
  );
};
